const ACTIVE_STATUSES = new Set([
  "queued",
  "running",
  "awaiting_location_review",
]);

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);
const POLLING_STATUSES = new Set(["queued", "running"]);

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const hasOwn = (value, key) =>
  isObject(value) && Object.prototype.hasOwnProperty.call(value, key);

const firstNonemptyString = (...values) =>
  values.find((value) => typeof value === "string" && value.trim())?.trim();

export function extractOnboardingRun(response) {
  const candidates = [
    response?.data?.redux?.payload?.run,
    response?.redux?.payload?.run,
    response?.data?.run,
    response?.run,
  ];
  const extracted = candidates.find(isObject);
  if (extracted) {
    return extracted;
  }

  // This makes the helper convenient for state initialized from an already
  // serialized run while avoiding mistaking an Axios response for a run.
  if (
    isObject(response) &&
    typeof response.status === "string" &&
    typeof response.uuid === "string"
  ) {
    return response;
  }
  return null;
}

export function createClientRequestId() {
  const cryptoApi = typeof crypto !== "undefined" ? crypto : undefined;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  } else {
    // The id is an idempotency key, not a secret. This fallback keeps the
    // action available in older browser/test environments without randomUUID.
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

export function isOnboardingRunActive(run) {
  return ACTIVE_STATUSES.has(run?.status);
}

export function isOnboardingRunTerminal(run) {
  return TERMINAL_STATUSES.has(run?.status);
}

export function shouldPollOnboardingRun(run) {
  return POLLING_STATUSES.has(run?.status);
}

const boundedNumber = (value, minimum, maximum, fallback = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }
  return Math.min(maximum, Math.max(minimum, number));
};

export function getOnboardingProgress(run) {
  const serverProgress = isObject(run?.progress) ? run.progress : {};
  let percent = boundedNumber(serverProgress.percent, 0, 100);
  const hasPersistedPlan = Array.isArray(run?.execution_history)
    ? run.execution_history.some((entry) => entry?.event === "plan_created")
    : false;

  // Planning is durable work already completed by the server. It is the only
  // supported floor when no stage item has completed yet; never invent a
  // larger head start.
  if (percent === 0 && hasPersistedPlan) {
    percent = 1;
  }

  return {
    ...serverProgress,
    percent,
    completed_units: boundedNumber(
      serverProgress.completed_units,
      0,
      Number.MAX_SAFE_INTEGER
    ),
    total_units: boundedNumber(
      serverProgress.total_units,
      0,
      Number.MAX_SAFE_INTEGER
    ),
    current_stage_key:
      serverProgress.current_stage_key || run?.current_stage || "",
    label: firstNonemptyString(serverProgress.label, "Setup"),
  };
}

const getPreflightRows = (preflightResults) => {
  const rows =
    preflightResults?.stores ||
    preflightResults?.locations ||
    preflightResults?.store_recommendations ||
    [];
  return Array.isArray(rows) ? rows.filter(isObject) : [];
};

const getGroupKeys = (preflightResults) => {
  const groups =
    preflightResults?.groups || preflightResults?.recommended_groups || [];
  const rawKeys = Array.isArray(groups)
    ? groups.map((group) => group?.key || group?.group_key)
    : isObject(groups)
    ? Object.keys(groups)
    : [];
  return rawKeys.filter(
    (groupKey, index) =>
      typeof groupKey === "string" &&
      groupKey &&
      rawKeys.indexOf(groupKey) === index
  );
};

const normalizeStoreId = (value) => {
  const storeId = Number(value);
  return Number.isInteger(storeId) ? storeId : null;
};

export function buildDefaultLocationDecisions(run) {
  const preflightResults = isObject(run?.preflight_results)
    ? run.preflight_results
    : {};
  const preflightRows = getPreflightRows(preflightResults);
  const rowsByStoreId = new Map();
  preflightRows.forEach((row) => {
    const storeId = normalizeStoreId(row.store_id);
    if (storeId !== null && !rowsByStoreId.has(storeId)) {
      rowsByStoreId.set(storeId, row);
    }
  });

  const orderedStoreIds = [];
  const seenStoreIds = new Set();
  const appendStoreId = (value) => {
    const storeId = normalizeStoreId(value);
    if (storeId !== null && !seenStoreIds.has(storeId)) {
      seenStoreIds.add(storeId);
      orderedStoreIds.push(storeId);
    }
  };
  (Array.isArray(run?.store_ids) ? run.store_ids : []).forEach(appendStoreId);
  preflightRows.forEach((row) => appendStoreId(row.store_id));

  const groupKeys = getGroupKeys(preflightResults);
  return orderedStoreIds.map((storeId) => {
    const hasPreflightRow = rowsByStoreId.has(storeId);
    const row = rowsByStoreId.get(storeId) || {};
    const recommendedGroupKey =
      row.recommended_group_key || row.assigned_group_key;
    const assignedGroupKey = !hasPreflightRow
      ? "portfolio_default"
      : groupKeys.includes(recommendedGroupKey)
        ? recommendedGroupKey
        : "";
    const reason = firstNonemptyString(
      row.reason,
      row.recommendation_reason,
      Array.isArray(row.reason_codes) ? row.reason_codes.join(", ") : "",
      "recommended_default"
    );

    return {
      store_id: storeId,
      excluded_from_fit: hasOwn(row, "recommended_excluded_from_fit")
        ? row.recommended_excluded_from_fit === true
        : row.excluded_from_fit === true,
      assigned_group_key: assignedGroupKey,
      reason,
    };
  });
}

export function getOnboardingErrorMessage(error, fallback) {
  return (
    firstNonemptyString(
      error?.response?.data?.system?.message,
      error?.response?.data?.detail,
      error?.response?.data?.error,
      typeof error === "string" ? error : "",
      error?.message,
      fallback
    ) || "Unable to update onboarding setup."
  );
}
